export type WidgetState = 'idle' | 'selecting' | 'form' | 'submitting' | 'success';

export interface SelectedElement {
  element: HTMLElement;
  selector: string;
  xpath: string;
  tag: string;
  classes: string[];
  id: string;
  text: string;
  boundingBox: { x: number; y: number; width: number; height: number };
  computedStyles: Record<string, string>;
  accessibility: { role: string | null; ariaLabel: string | null; tabindex: string | null };
  screenshot?: string;
}

export interface WidgetStore {
  state: WidgetState;
  selectedElements: SelectedElement[];
  metadata: Record<string, unknown>;
  context: Record<string, unknown>;
}

const store: WidgetStore = {
  state: 'idle',
  selectedElements: [],
  metadata: {},
  context: {},
};

type Listener = (store: WidgetStore) => void;
const listeners: Listener[] = [];

export function getStore(): WidgetStore {
  return store;
}

export function setState(state: WidgetState): void {
  store.state = state;
  notify();
}

export function addSelectedElement(el: SelectedElement): void {
  store.selectedElements.push(el);
  notify();
}

export function removeSelectedElement(element: HTMLElement): void {
  store.selectedElements = store.selectedElements.filter(e => e.element !== element);
  notify();
}

export function isElementSelected(element: HTMLElement): boolean {
  return store.selectedElements.some(e => e.element === element);
}

export function clearSelectedElements(): void {
  store.selectedElements = [];
  notify();
}

export function setMetadata(meta: Record<string, unknown>): void {
  store.metadata = { ...store.metadata, ...meta };
}

export function addContext(key: string, value: unknown): void {
  store.context[key] = value;
}

export function resetStore(): void {
  store.state = 'idle';
  store.selectedElements = [];
  store.context = {};
  notify();
}

export function subscribe(fn: Listener): () => void {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

function notify(): void {
  for (const fn of listeners) {
    fn(store);
  }
}
